import { useMutation } from '@apollo/client/react'
import { ModalForm, Select, useForm } from '@faceup/form'
import { Button, Typography, useMessage } from '@faceup/ui-base'
import { ChannelDeletionOutcome } from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import type { HotlineCountry } from '../__generated__/graphql'

const fragments = {
  TransferHotlineNumberModal_hotline: graphql(`
    fragment TransferHotlineNumberModal_hotline on ReportSource {
      id
      name
      config {
        ... on AutomatedHotlineReportSourceConfiguration {
          id
          phoneNumber
          country
        }
        ... on AiHotlineReportSourceConfiguration {
          id
          phoneNumber
          country
        }
      }
    }
  `),
}

export type TransferableHotlineFragment = FragmentType<
  typeof fragments.TransferHotlineNumberModal_hotline
>

const mutation = graphql(`
  mutation TransferHotlineNumberMutation($input: TransferHotlineNumberInput!) {
    transferHotlineNumber(input: $input) {
      sourceOutcome
    }
  }
`)

const hotlineTypeLabels: Record<
  'AiHotlineReportSourceConfiguration' | 'AutomatedHotlineReportSourceConfiguration',
  string
> = {
  AiHotlineReportSourceConfiguration: 'AI',
  AutomatedHotlineReportSourceConfiguration: 'Automated',
}

const schema = yup.object({
  sourceChannelId: yup.string().required(),
})

type TransferHotlineNumberModalProps = {
  targetChannelId: string
  targetName: string
  targetCountry: HotlineCountry
  hotlines: TransferableHotlineFragment[]
  refetch: () => void
}

export const TransferHotlineNumberModal = ({
  targetChannelId,
  targetName,
  targetCountry,
  hotlines: _hotlines,
  refetch,
}: TransferHotlineNumberModalProps) => {
  const [open, setOpen] = useState(false)
  const message = useMessage()

  const form = useForm({
    schema,
    afterSubmit: 'resetValues',
    defaultValues: { sourceChannelId: undefined },
  })

  const hotlines = getFragmentData(fragments.TransferHotlineNumberModal_hotline, _hotlines)

  const [transferHotlineNumber] = useMutation(mutation, {
    onCompleted: ({ transferHotlineNumber: result }) => {
      setOpen(false)
      message.success(
        result?.sourceOutcome === ChannelDeletionOutcome.Archived
          ? `Number moved to ${targetName}. The hotline it came from still has cases, so it was archived.`
          : `Number moved to ${targetName}. The hotline it came from was deleted.`
      )
      refetch()
    },
  })

  const sources = hotlines
    .map(hotline => {
      const configuration =
        hotline.config?.__typename === 'AiHotlineReportSourceConfiguration' ||
        hotline.config?.__typename === 'AutomatedHotlineReportSourceConfiguration'
          ? hotline.config
          : null

      if (
        !configuration?.phoneNumber ||
        configuration.country !== targetCountry ||
        hotline.id === targetChannelId
      ) {
        return null
      }

      return {
        value: hotline.id,
        label: `${configuration.phoneNumber} - ${hotline.name} (${hotlineTypeLabels[configuration.__typename]})`,
      }
    })
    .filter(source => source !== null)

  if (sources.length === 0) {
    return null
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>Transfer number</Button>
      <ModalForm
        form={form}
        open={open}
        title={`Transfer a number to ${targetName}`}
        customSubmitButtonText='Transfer'
        isSuccessMessageDisplayed={false}
        onClose={() => setOpen(false)}
        onSubmit={async ({ sourceChannelId }) => {
          const result = await transferHotlineNumber({
            variables: { input: { sourceChannelId, targetChannelId } },
          })

          return !result.error
        }}
      >
        <Typography.Text>
          The number keeps ringing, it only starts ringing here instead. The hotline it comes from
          is archived if it still has cases and deleted if it does not - the same as deleting it.
        </Typography.Text>
        <Select
          control={form.control}
          name='sourceChannelId'
          label='Take the number from'
          placeholder='Select a hotline'
          options={sources}
        />
      </ModalForm>
    </>
  )
}
